import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { cssVar, darkThemeStylesheet, lightThemeStylesheet } from '@fabric-app/ui/theme/variables';
import { useResourceEmailDataContextSafe } from '../../../context/resourceDataContext.js';
import { styled } from 'styled-components';
import { useMemo, useState } from 'react';
import { Flex } from '@fabric-app/ui/components/Flex';
import { baseHtmlBasedPreviewStyles } from '@fabric-app/ui/html/html.config';
import { useColorScheme } from '@fabric-app/ui/theme/useColorScheme';
import { mediaMobile, cssTextEllipsis } from '@fabric-app/ui/constants/styled-utils';
import { IconTextEditorInEditorToggleList } from '@fabric-app/icons/svg';
import { useBoolState } from '@fabric-app/hooks/useBooleanState';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { ResourceEmailAttachments } from './ResourceEmailAttachments.js';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import { AnimatePresence, motion } from 'framer-motion';
import { framerAnimationFade } from '@fabric-app/ui/constants/framerAnimations';
import { isTruthy } from '@fabric-app/utils/guards';
import { useQueryResourceAssets } from '../../../../queries/useQueryResourceAssets.js';
import dayjs from 'dayjs';

const MORE_META_KEYS = [
  "cc",
  "bcc"
];
const FRID_UUID_REGEX = /src="frid:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})"/g;
const ResourceEmailContent = () => {
  const { resource } = useResourceEmailDataContextSafe();
  const boolState = useBoolState();
  const { metaValues, moreMetaValues } = useMemo(() => {
    return {
      metaValues: [
        {
          key: "from",
          value: resource.data.emailMessage?.from
        },
        {
          key: "to",
          value: resource.data.emailMessage?.to
        },
        {
          key: "date",
          value: dayjs(resource.createdAt).format("lll")
        },
        {
          key: "subject",
          value: resource.data.emailMessage?.subject
        }
      ],
      moreMetaValues: MORE_META_KEYS.map((key) => {
        return {
          key,
          value: resource.data.emailMessage?.[key]
        };
      }).filter((item) => isTruthy(item.value))
    };
  }, [
    resource.data.emailMessage,
    resource.createdAt
  ]);
  const { data: resourceAssets, isLoading: isLoadingResourceAssets } = useQueryResourceAssets({
    resource
  });
  const attachments = resourceAssets?.data.assets;
  const contentData = useMemo(() => {
    const hasInlineImages = !!resource.data.emailMessage?.body?.includes('src="frid:');
    if (!resource.data.emailMessage?.body || !attachments || attachments.length === 0) {
      return {
        content: resource.data.emailMessage?.body,
        hasInlineImages
      };
    }
    let result = resource.data.emailMessage.body;
    let match = null;
    while ((match = FRID_UUID_REGEX.exec(result)) !== null) {
      const toReplace = match[0];
      const uuid = match[1];
      const attachment = attachments?.find((asset) => asset.id === uuid);
      if (attachment && attachment.thumbnail?.lg) {
        result = result.replace(toReplace, `src="${attachment.thumbnail?.lg}"`);
      }
    }
    return {
      content: result,
      hasInlineImages
    };
  }, [
    resource.data.emailMessage?.body,
    attachments
  ]);
  return /* @__PURE__ */ jsx(ScrollArea.Default, {
    scrollbarVariant: "subtle",
    innerDisplayFlex: true,
    asFlex: true,
    children: /* @__PURE__ */ jsxs(Container, {
      children: [
        metaValues.length + moreMetaValues.length > 0 && /* @__PURE__ */ jsxs(MetaContainer, {
          children: [
            metaValues.map(({ key, value }) => /* @__PURE__ */ jsxs(MetaItem, {
              children: [
                /* @__PURE__ */ jsxs(MetaItemKey, {
                  color: "tertiary",
                  children: [
                    key,
                    ": "
                  ]
                }),
                /* @__PURE__ */ jsx("span", {
                  children: value
                })
              ]
            }, key)),
            moreMetaValues.length > 0 && /* @__PURE__ */ jsxs(Fragment, {
              children: [
                /* @__PURE__ */ jsxs(Flex, {
                  alignItems: "center",
                  gap: "text",
                  onClick: boolState.handleToggle,
                  children: [
                    /* @__PURE__ */ jsx(MoreContentIcon, {
                      expanded: boolState.value,
                      children: /* @__PURE__ */ jsx(IconTextEditorInEditorToggleList, {})
                    }),
                    /* @__PURE__ */ jsx(MetaItemKey, {
                      children: "More: "
                    })
                  ]
                }),
                boolState.value && /* @__PURE__ */ jsx(Fragment, {
                  children: moreMetaValues.map(({ key, value }) => /* @__PURE__ */ jsxs(MetaItem, {
                    children: [
                      /* @__PURE__ */ jsxs(MetaItemKey, {
                        color: "tertiary",
                        children: [
                          key,
                          ": "
                        ]
                      }),
                      /* @__PURE__ */ jsx("span", {
                        children: value
                      })
                    ]
                  }, key))
                })
              ]
            })
          ]
        }),
        contentData.content ? /* @__PURE__ */ jsx(SandboxedContent, {
          html: contentData.content,
          forcedLoadingState: contentData.hasInlineImages && isLoadingResourceAssets
        }) : null,
        /* @__PURE__ */ jsx(ResourceEmailAttachments, {
          resources: resourceAssets?.data.assets
        })
      ]
    })
  });
};
const Container = styled(Flex).attrs({
  direction: "column",
  gap: 12
}).withConfig({
  displayName: "ResourceEmailContent__Container",
  componentId: "sc-de9735d4-0"
})([
  `margin:0 auto;max-width:700px;width:100%;padding:50px 1rem;`,
  `{padding:1rem;padding-bottom:50px;}`
], mediaMobile);
const MetaContainer = styled.div.withConfig({
  displayName: "ResourceEmailContent__MetaContainer",
  componentId: "sc-de9735d4-1"
})([
  `padding:0.625rem;background-color:rgba(`,
  `,0.6);display:flex;flex-direction:column;gap:0.625rem;border-radius:0.75rem;`
], cssVar["color-bg-tertiary-rgb"]);
const MetaItem = styled.div.withConfig({
  displayName: "ResourceEmailContent__MetaItem",
  componentId: "sc-de9735d4-2"
})([
  `color:rgba(`,
  `,0.7);`,
  `;`
], cssVar["color-text-primary-rgb"], cssTextEllipsis);
const MetaItemKey = styled.span.withConfig({
  displayName: "ResourceEmailContent__MetaItemKey",
  componentId: "sc-de9735d4-3"
})([
  `font-weight:500;color:rgba(`,
  `,0.7);`
], cssVar["color-text-primary-rgb"]);
const MoreContentIcon = styled.button.withConfig({
  displayName: "ResourceEmailContent__MoreContentIcon",
  componentId: "sc-de9735d4-4"
})([
  `background:none;border:none;padding:0;margin:0;cursor:pointer;width:22px;height:22px;display:flex;align-items:center;justify-content:center;transition:0.2s transform;transform:`,
  `;`
], ({ expanded }) => expanded ? "rotate(0deg)" : "rotate(-90deg)");
const SandboxedContent = ({ html, onLoad, forcedLoadingState }) => {
  const { parsedColorScheme } = useColorScheme();
  const [iframeHeight, setIframeHeight] = useState(0);
  return /* @__PURE__ */ jsxs(AnimatePresence, {
    children: [
      (iframeHeight === 0 || forcedLoadingState) && /* @__PURE__ */ jsx(Flex, {
        as: motion.div,
        ...framerAnimationFade,
        alignItems: "center",
        justifyContent: "center",
        style: {
          padding: "2rem"
        },
        children: /* @__PURE__ */ jsx(Spinner, {
          size: 32,
          thickness: 3
        })
      }, "loader"),
      /* @__PURE__ */ jsx("iframe", {
        style: {
          width: "100%",
          opacity: iframeHeight > 0 ? 1 : 0,
          height: iframeHeight,
          transition: "0.2s opacity"
        },
        /**
        * allow-same-origin - allows us to read the inner height
        */
        sandbox: "allow-popups allow-popups-to-escape-sandbox allow-same-origin",
        // adjust height to same size as the content
        onLoad: (e) => {
          if (e.currentTarget.contentWindow) {
            setIframeHeight(e.currentTarget.contentWindow.document.documentElement.scrollHeight + 15);
          }
          onLoad?.();
        },
        srcDoc: `
        <head>
        <base target="_blank">
        </head>
        <style>
        // Generated using:
// https://gwfh.mranftl.com/fonts/inter?subsets=latin

/* inter-100 - latin */
@font-face {
  font-display: swap; /* Check https://developer.mozilla.org/en-US/docs/Web/CSS/@font-face/font-display for other options. */
  font-family: 'Inter';
  font-style: normal;
  font-weight: 100;
  src: url('/fonts/inter-v13-latin-100.woff2') format('woff2'); /* Chrome 36+, Opera 23+, Firefox 39+, Safari 12+, iOS 10+ */
}
/* inter-200 - latin */
@font-face {
  font-display: swap; /* Check https://developer.mozilla.org/en-US/docs/Web/CSS/@font-face/font-display for other options. */
  font-family: 'Inter';
  font-style: normal;
  font-weight: 200;
  src: url('/fonts/inter-v13-latin-200.woff2') format('woff2'); /* Chrome 36+, Opera 23+, Firefox 39+, Safari 12+, iOS 10+ */
}
/* inter-300 - latin */
@font-face {
  font-display: swap; /* Check https://developer.mozilla.org/en-US/docs/Web/CSS/@font-face/font-display for other options. */
  font-family: 'Inter';
  font-style: normal;
  font-weight: 300;
  src: url('/fonts/inter-v13-latin-300.woff2') format('woff2'); /* Chrome 36+, Opera 23+, Firefox 39+, Safari 12+, iOS 10+ */
}
/* inter-regular - latin */
@font-face {
  font-display: swap; /* Check https://developer.mozilla.org/en-US/docs/Web/CSS/@font-face/font-display for other options. */
  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  src: url('/fonts/inter-v13-latin-regular.woff2') format('woff2'); /* Chrome 36+, Opera 23+, Firefox 39+, Safari 12+, iOS 10+ */
}
/* inter-500 - latin */
@font-face {
  font-display: swap; /* Check https://developer.mozilla.org/en-US/docs/Web/CSS/@font-face/font-display for other options. */
  font-family: 'Inter';
  font-style: normal;
  font-weight: 500;
  src: url('/fonts/inter-v13-latin-500.woff2') format('woff2'); /* Chrome 36+, Opera 23+, Firefox 39+, Safari 12+, iOS 10+ */
}
/* inter-600 - latin */
@font-face {
  font-display: swap; /* Check https://developer.mozilla.org/en-US/docs/Web/CSS/@font-face/font-display for other options. */
  font-family: 'Inter';
  font-style: normal;
  font-weight: 600;
  src: url('/fonts/inter-v13-latin-600.woff2') format('woff2'); /* Chrome 36+, Opera 23+, Firefox 39+, Safari 12+, iOS 10+ */
}
/* inter-700 - latin */
@font-face {
  font-display: swap; /* Check https://developer.mozilla.org/en-US/docs/Web/CSS/@font-face/font-display for other options. */
  font-family: 'Inter';
  font-style: normal;
  font-weight: 700;
  src: url('/fonts/inter-v13-latin-700.woff2') format('woff2'); /* Chrome 36+, Opera 23+, Firefox 39+, Safari 12+, iOS 10+ */
}
/* inter-800 - latin */
@font-face {
  font-display: swap; /* Check https://developer.mozilla.org/en-US/docs/Web/CSS/@font-face/font-display for other options. */
  font-family: 'Inter';
  font-style: normal;
  font-weight: 800;
  src: url('/fonts/inter-v13-latin-800.woff2') format('woff2'); /* Chrome 36+, Opera 23+, Firefox 39+, Safari 12+, iOS 10+ */
}
/* inter-900 - latin */
@font-face {
  font-display: swap; /* Check https://developer.mozilla.org/en-US/docs/Web/CSS/@font-face/font-display for other options. */
  font-family: 'Inter';
  font-style: normal;
  font-weight: 900;
  src: url('/fonts/inter-v13-latin-900.woff2') format('woff2'); /* Chrome 36+, Opera 23+, Firefox 39+, Safari 12+, iOS 10+ */
}

        :root {
          ${parsedColorScheme === "dark" ? darkThemeStylesheet.join("\n") : lightThemeStylesheet.join("\n")}
          }
        </style>
        ${html}
          <style>
            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            img {
              max-width: 100%;
            }
            html, body {
              width: 100%;
            }
            body {
              font-family: Inter, sans-serif;
              font-size: 16px;
              color: ${cssVar["color-text-primary"]};
              background-color: ${cssVar["color-bg-primary"]};
              font-weight: 300;
            }
            ${baseHtmlBasedPreviewStyles}

            table {
              table-layout: fixed;
              max-width: 100%;
              border-collapse: initial;
            }
            h1, h2, h3, h4, h5, h6, p, ul, ol {
              line-height: 1.5;
            }
            h1:not(:first-child), h2:not(:first-child), h3:not(:first-child), h4:not(:first-child), h5:not(:first-child), h6:not(:first-child), p:not(:first-child), ul:not(:first-child), ol:not(:first-child) {
              margin-top: 1.5rem;
            }
            a {
              color: ${cssVar["color-app-primary-text"]};
            }
          </style>
      `
      }, "iframe")
    ]
  });
};

export { ResourceEmailContent };
//# sourceMappingURL=ResourceEmailContent.js.map
